/**
 * Settings Page
 * Parent settings for managing children and preferences
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Shield, Clock, Eye, Trash2, Save, ArrowLeft, Plus, X, Volume2 } from 'lucide-react';
import { useSoundEffects } from '../hooks/useSoundEffects';
import { useSoundSettings } from '../hooks/useSoundSettings';
import { SoundEngine } from '../audio';
import { getChildren, getSettings, updateSettings } from '../services/childService';
import { getErrorMessage } from '../services/api';
import ManageHabits from '../components/ManageHabits';
import MindoraCharacter from '../components/character/MindoraCharacter';
import type { ChildProfileWithConsent, ParentSettings } from '@kids-lms/types';

export default function SettingsPage() {
  const { childId } = useParams<{ childId?: string }>();
  const [children, setChildren] = useState<ChildProfileWithConsent[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(childId || null);
  const [settings, setSettings] = useState<ParentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [dailyLimit, setDailyLimit] = useState(60);

  // Sound
  const { play } = useSoundEffects();
  const soundSettings = useSoundSettings();

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChildId) {
      loadSettings(selectedChildId);
    }
  }, [selectedChildId]);

  const loadChildren = async () => {
    try {
      const data = await getChildren();
      setChildren(data);
      if (!selectedChildId && data.length > 0) {
        setSelectedChildId(data[0].id);
      }
      if (data.length === 0) {
        setLoading(false);
      }
    } catch (err) {
      setError(getErrorMessage(err));
      setLoading(false);
    }
  };

  const loadSettings = async (id: string) => {
    try {
      setLoading(true);
      const data = await getSettings(id);
      setSettings(data);
    } catch {
      // Settings might not exist yet, use defaults
      setSettings({
        id: '',
        childId: id,
        allowedTopics: [],
        blockedKeywords: [],
        notificationPreferences: {
          dailySummary: true,
          weeklyReport: true,
          flaggedContent: true,
          sessionStart: false,
          timeLimit: true,
        },
        pushTokenIos: null,
        pushTokenAndroid: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedChildId || !settings) return;

    try {
      setSaving(true);
      setError('');
      await updateSettings(selectedChildId, {
        allowedTopics: settings.allowedTopics,
        blockedKeywords: settings.blockedKeywords,
        notificationPreferences: settings.notificationPreferences,
      });
      setSuccess('Settings saved successfully!');
      play('success');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const addBlockedKeyword = () => {
    if (newKeyword.trim() && settings) {
      setSettings({
        ...settings,
        blockedKeywords: [...(settings.blockedKeywords || []), newKeyword.trim().toLowerCase()],
      });
      setNewKeyword('');
    }
  };

  const removeBlockedKeyword = (keyword: string) => {
    if (settings) {
      setSettings({
        ...settings,
        blockedKeywords: (settings.blockedKeywords || []).filter(k => k !== keyword),
      });
    }
  };

  const toggleNotification = (key: keyof NonNullable<ParentSettings['notificationPreferences']>) => {
    if (settings && settings.notificationPreferences) {
      play('toggle');
      setSettings({
        ...settings,
        notificationPreferences: {
          ...settings.notificationPreferences,
          [key]: !settings.notificationPreferences[key],
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-kid-ocean border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/parent"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>

      <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-8">
        Settings
      </h1>

      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded-xl">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 rounded-xl">
          {success}
        </div>
      )}

      {children.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Children Added</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Add a child profile first to configure settings.</p>
          <Link
            to="/parent/add-child"
            className="inline-flex items-center gap-2 px-4 py-2 bg-kid-ocean text-white rounded-lg hover:bg-kid-ocean/90"
          >
            <Plus className="w-4 h-4" />
            Add Child
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Child Selector */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Child
            </label>
            <select
              value={selectedChildId || ''}
              onChange={(e) => setSelectedChildId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-kid-ocean"
            >
              {children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          </div>

          {/* Notifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                Notifications
              </h2>
            </div>

            <div className="space-y-4">
              {[
                { key: 'dailySummary' as const, label: 'Daily Summary', description: 'Receive a daily report of your child\'s learning' },
                { key: 'weeklyReport' as const, label: 'Weekly Report', description: 'Get a weekly progress summary' },
                { key: 'flaggedContent' as const, label: 'Flagged Content Alerts', description: 'Immediate notification when content is flagged' },
                { key: 'sessionStart' as const, label: 'Session Start', description: 'Notify when your child starts a learning session' },
                { key: 'timeLimit' as const, label: 'Time Limit Alerts', description: 'Get notified when daily time limit is reached' },
              ].map(({ key, label, description }) => (
                <label key={key} className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">{label}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings?.notificationPreferences?.[key] ?? false}
                    onChange={() => toggleNotification(key)}
                    className="w-5 h-5 rounded text-kid-ocean focus:ring-kid-ocean"
                  />
                </label>
              ))}
            </div>
          </motion.div>

          {/* Sound & Audio */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-kid-violet/10 rounded-kid-sm">
                <Volume2 className="w-5 h-5 text-kid-violet" />
              </div>
              <h2 className="text-xl font-display text-kid-text">
                Sound & Audio
              </h2>
              <MindoraCharacter mood="happy" size="sm" className="ml-auto" />
            </div>

            <div className="space-y-5">
              {/* Master Volume */}
              <div>
                <label className="font-medium text-gray-800 dark:text-white block mb-2">
                  Master Volume
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(soundSettings.masterVolume * 100)}
                  onChange={(e) => {
                    const v = Number(e.target.value) / 100;
                    soundSettings.setMasterVolume(v);
                    SoundEngine.getInstance().setMasterVolume(v);
                  }}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer accent-violet-500"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {Math.round(soundSettings.masterVolume * 100)}%
                </p>
              </div>

              {/* SFX Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">Sound Effects</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tap sounds, chimes, and feedback</p>
                </div>
                <input
                  type="checkbox"
                  checked={soundSettings.sfxEnabled}
                  onChange={() => { play('toggle'); soundSettings.toggleSfx(); }}
                  className="w-5 h-5 rounded text-violet-500 focus:ring-violet-500"
                />
              </label>

              {/* Companion Sounds Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">Companion Sounds</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Mindora's expressive reactions</p>
                </div>
                <input
                  type="checkbox"
                  checked={soundSettings.companionEnabled}
                  onChange={() => { play('toggle'); soundSettings.toggleCompanion(); }}
                  className="w-5 h-5 rounded text-violet-500 focus:ring-violet-500"
                />
              </label>

              {/* Ambient Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">Ambient Soundscapes</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Background sounds by time of day</p>
                </div>
                <input
                  type="checkbox"
                  checked={soundSettings.ambientEnabled}
                  onChange={() => { play('toggle'); soundSettings.toggleAmbient(); }}
                  className="w-5 h-5 rounded text-violet-500 focus:ring-violet-500"
                />
              </label>

              {/* Quiet Hours */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <label className="flex items-center justify-between cursor-pointer mb-3">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">Quiet Hours</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Mute all sounds during set hours</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={soundSettings.quietHours.enabled}
                    onChange={() => {
                      play('toggle');
                      soundSettings.setQuietHours({ enabled: !soundSettings.quietHours.enabled });
                    }}
                    className="w-5 h-5 rounded text-violet-500 focus:ring-violet-500"
                  />
                </label>
                {soundSettings.quietHours.enabled && (
                  <div className="flex gap-4 ml-2">
                    <div>
                      <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">Start</label>
                      <input
                        type="time"
                        value={soundSettings.quietHours.start}
                        onChange={(e) => soundSettings.setQuietHours({ start: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">End</label>
                      <input
                        type="time"
                        value={soundSettings.quietHours.end}
                        onChange={(e) => soundSettings.setQuietHours({ end: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Safety Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                Safety Settings
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="font-medium text-gray-800 dark:text-white block mb-2">
                  Custom Blocked Keywords
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Add words or phrases that should be blocked in conversations
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBlockedKeyword())}
                    placeholder="Add keyword..."
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={addBlockedKeyword}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 touch-slop"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(settings?.blockedKeywords || []).map((keyword) => (
                    <span
                      key={keyword}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm"
                    >
                      {keyword}
                      <button
                        onClick={() => removeBlockedKeyword(keyword)}
                        className="hover:text-red-900 dark:hover:text-red-300 p-2 touch-slop rounded-full"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Time Limits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                Time Limits
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="font-medium text-gray-800 dark:text-white block mb-2">
                  Daily Learning Limit
                </label>
                <select
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(Number(e.target.value))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                  <option value={120}>2 hours</option>
                  <option value={180}>3 hours</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Daily Habits */}
          {selectedChildId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <span className="text-lg">🔥</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">Daily Habits</h2>
                  <p className="text-xs text-gray-400">Add or remove daily habits for {children.find(c => c.id === selectedChildId)?.name}</p>
                </div>
              </div>
              <ManageHabits childId={selectedChildId} />
            </motion.div>
          )}

          {/* Privacy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <Eye className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                Privacy
              </h2>
            </div>

            <div className="space-y-4">
              <button className="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <p className="font-medium text-gray-800 dark:text-white">Download My Data</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Get a copy of all your data
                </p>
              </button>

              <button className="w-full py-3 px-4 border border-red-300 dark:border-red-600 rounded-xl text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <Trash2 className="w-4 h-4" />
                  <p className="font-medium">Delete Account</p>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Permanently delete your account and all data
                </p>
              </button>
            </div>
          </motion.div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-kid-ocean text-white rounded-xl hover:bg-kid-ocean/90 disabled:opacity-50 transition-colors"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
